import { icon } from '../../shared/icons/render.js';
/**
 * Ticket Checkout Success - Inline Success/Error Box
 * Plattform-konsistentes Design mit Glassmorphism
 *
 * @module tickets/ticket-checkout-success
 */

// Safe fallback: __ may not be loaded yet on event-detail templates
const __ = (typeof window.__ === 'function')
  ? window.__
  : function(message, replacements) {
    const text = String(message == null ? '' : message);
    if (!Array.isArray(replacements) || replacements.length === 0) return text;
    return text.replace(/\{(\d+)\}/g, (match, idx) => {
      const value = replacements[Number(idx)];
      return value === undefined || value === null ? match : String(value);
    });
  };

// SVG Icons (alle als inline SVG, keine Emojis)
const ICONS = {
  // Status Icons
  success: `${icon('checkCircle')}`,

  pending: `${icon('clock')}`,

  error: `${icon('circleX')}`,

  info: `${icon('info')}`,

  cancelled: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <path d="M9 15l6-6M9 9l6 6"/>
  </svg>`,

  // Info Card Icons
  email: `${icon('mail', { className: 'checkout-outcome-info-icon' })}`,

  ticket: `${icon('ticket', { className: 'checkout-outcome-info-icon' })}`,

  // Button Icons
  ticketBtn: `${icon('ticket')}`,

  arrow: `${icon('arrowRight')}`,

  retry: `${icon('refreshCw')}`,
};

// Status-Konfiguration mit ARIA-Labels
const STATUS_CONFIG = {
  success: {
    icon: ICONS.success,
    ariaLabel: __('Erfolg'),
    defaultTitle: __('Erfolgreich!'),
  },
  pending: {
    icon: ICONS.pending,
    ariaLabel: __('Ausstehend'),
    defaultTitle: __('Warte auf Bestätigung'),
  },
  error: {
    icon: ICONS.error,
    ariaLabel: __('Fehler'),
    defaultTitle: __('Fehler aufgetreten'),
  },
  info: {
    icon: ICONS.info,
    ariaLabel: __('Information'),
    defaultTitle: __('Hinweis'),
  },
  cancelled: {
    icon: ICONS.cancelled,
    ariaLabel: __('Abgebrochen'),
    defaultTitle: __('Buchung abgebrochen'),
  },
};

/**
 * Rendert die Inline Success-Box und ersetzt den Container-Inhalt
 *
 * @param {HTMLElement} container - Container-Element (z.B. .ticket-checkout-inline)
 * @param {Object} options - Konfiguration
 * @param {string} options.type - 'success' | 'pending' | 'error' | 'info' | 'cancelled'
 * @param {string} options.title - Titel
 * @param {string} options.message - Nachricht
 * @param {string} [options.email] - E-Mail für Bestaetigung
 * @param {string} [options.ticketName] - Ticket-Name
 * @param {string} [options.eventSlug] - Event-Slug für Links
 * @param {boolean} [options.isLoggedIn] - User eingeloggt?
 * @param {string} [options.ticketCenterUrl] - Guest-Ticket-URL (z.B. /ticket/view#token=...)
 * @param {boolean} [options.showEventLink] - "Zum Event" Link anzeigen
 * @param {Function} [options.onClose] - Callback beim Schliessen
 * @param {Function} [options.onRetry] - Callback für "Erneut versuchen" (bei Error)
 */
function renderCheckoutSuccess(container, options) {
  if (!container) return;

  try {
    const {
      type = 'success',
      title = STATUS_CONFIG[type]?.defaultTitle || __('Erfolgreich!'),
      message = '',
      email = '',
      ticketName = '',
      eventSlug = '',
      isLoggedIn = false,
      ticketCenterUrl = '',
      showEventLink = true,
      onClose = null,
      onRetry = null,
    } = options;

    const config = STATUS_CONFIG[type] || STATUS_CONFIG.success;

    // HTML mit neuer Glass Card Struktur generieren
    const html = `
      <div class="checkout-outcome-box"
           data-checkout-success
           data-status="${escapeHtml(type)}"
           role="status"
           aria-live="assertive"
           aria-label="${config.ariaLabel}">

        <!-- Hintergrund-Glow -->
        <div class="checkout-outcome-glow" aria-hidden="true"></div>

        <!-- Glass Card Container -->
        <div class="checkout-outcome-card">
          <!-- Icon mit Status-Ring -->
          <div class="checkout-outcome-icon-container">
            <div class="checkout-outcome-icon-ring" aria-hidden="true"></div>
            <div class="checkout-outcome-icon" aria-hidden="true">
              ${config.icon}
            </div>
          </div>

          <!-- Content -->
          <div class="checkout-outcome-content">
            <h3 class="checkout-outcome-title">${escapeHtml(title)}</h3>
            ${message ? `<p class="checkout-outcome-message">${escapeHtml(message)}</p>` : ''}

            ${(email || ticketName) ? `
            <!-- Info Cards Stack -->
            <div class="checkout-outcome-info-stack">
              ${email ? renderInfoCard('email', __('Bestätigung gesendet an'), email) : ''}
              ${ticketName ? renderInfoCard('ticket', __('Ticket'), ticketName) : ''}
            </div>
            ` : ''}
          </div>

          <!-- Action Buttons -->
          <div class="checkout-outcome-actions">
            ${renderButtons(type, isLoggedIn, eventSlug, showEventLink, onClose, onRetry, ticketCenterUrl)}
          </div>
        </div>
      </div>
    `;

    // Container aktualisieren
    container.innerHTML = html;
    container.classList.add('checkout-outcome-visible');

    // Event-Listener hinzufügen
    attachEventListeners(container, onClose, onRetry);

    // CKO-2.2 (P2 H.1): Floating-Banner-Caller koennen Auto-Focus + Scroll
    // unterdruecken — beim Cancel-Recovery-Banner auf Event-Detail wuerde
    // Auto-Focus-Steal aggressiv wirken (passive UX, User explorieren noch).
    const suppressIntrusion = options && options.suppressAutoFocus === true;

    if (!suppressIntrusion) {
      // Scroll in View (nice-to-have, may fail on some browsers/devices)
      try {
        container.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) { /* silent */ }

      // Focus Management (Accessibility)
      const firstButton = container.querySelector('.checkout-outcome-btn');
      if (firstButton) {
        setTimeout(() => firstButton.focus(), 300);
      }
    }
  } catch (e) {
    if (window.console) console.error('renderCheckoutSuccess failed:', e);
    container.textContent = (options && options.message) || '';
  }
}

/**
 * Rendert eine Info-Card mit Icon, Label und Value
 */
function renderInfoCard(type, label, value) {
  const icon = type === 'email' ? ICONS.email : ICONS.ticket;

  return `
    <div class="checkout-outcome-info-card checkout-outcome-info-card--${type}">
      ${icon}
      <div class="checkout-outcome-info-content">
        <span class="checkout-outcome-info-label">${escapeHtml(label)}</span>
        <span class="checkout-outcome-info-value">${escapeHtml(value)}</span>
      </div>
    </div>
  `;
}

/**
 * Rendert die Buttons je nach Typ und Login-Status
 */
function renderButtons(type, isLoggedIn, eventSlug, showEventLink, onClose, onRetry, ticketCenterUrl) {
  const buttons = [];

  if (type === 'error') {
    // Error: "Erneut versuchen" + "Schliessen"
    if (onRetry) {
      buttons.push(`
        <button type="button"
                class="checkout-outcome-btn checkout-outcome-btn--primary"
                data-action="retry">
          ${ICONS.retry}
          <span>${__('Erneut versuchen')}</span>
        </button>
      `);
    }
    buttons.push(`
      <button type="button"
              class="checkout-outcome-btn checkout-outcome-btn--secondary"
              data-action="close">
        <span>${__('Schließen')}</span>
      </button>
    `);
  } else if (type === 'cancelled') {
    // Cancelled: "Erneut versuchen" + "Zurück zum Event"
    if (onRetry) {
      buttons.push(`
        <button type="button"
                class="checkout-outcome-btn checkout-outcome-btn--primary"
                data-action="retry">
          ${ICONS.retry}
          <span>${__('Erneut versuchen')}</span>
        </button>
      `);
    }
    if (showEventLink && eventSlug) {
      buttons.push(`
        <a href="/event/${escapeHtml(eventSlug)}"
           class="checkout-outcome-btn checkout-outcome-btn--secondary">
          <span>${__('Zurück zum Event')}</span>
          ${ICONS.arrow}
        </a>
      `);
    }
    // Fallback: Schliessen-Button wenn keine anderen Buttons vorhanden
    if (buttons.length === 0) {
      buttons.push(`
        <button type="button"
                class="checkout-outcome-btn checkout-outcome-btn--secondary"
                data-action="close">
          <span>${__('Schließen')}</span>
        </button>
      `);
    }
  } else if (type === 'pending') {
    // Pending (Genehmigung): "Verstanden"
    buttons.push(`
      <button type="button"
              class="checkout-outcome-btn checkout-outcome-btn--primary"
              data-action="close">
        <span>${__('Verstanden')}</span>
      </button>
    `);
    if (showEventLink && eventSlug) {
      buttons.push(`
        <a href="/event/${escapeHtml(eventSlug)}"
           class="checkout-outcome-btn checkout-outcome-btn--secondary">
          <span>${__('Zum Event')}</span>
          ${ICONS.arrow}
        </a>
      `);
    }
  } else {
    // Success: Primary CTA "Zum Event", sekundärer dezenter Link "Mein Ticket ansehen".
    if (eventSlug) {
      buttons.push(`
        <a href="/event/${escapeHtml(eventSlug)}"
           class="checkout-outcome-btn checkout-outcome-btn--primary">
          ${ICONS.arrow}
          <span>${__('Zum Event')}</span>
        </a>
      `);
    }

    // Optional: Schliessen-Button (Secondary, vor Footer-Link)
    if (onClose) {
      buttons.push(`
        <button type="button"
                class="checkout-outcome-btn checkout-outcome-btn--secondary"
                data-action="close">
          <span>${__('Schließen')}</span>
        </button>
      `);
    }

    // Dezenter Footer-Link "Mein Ticket ansehen" — bewusst am Ende der Box,
    // nicht in Konkurrenz zum Primary-CTA. Fallback /event/{slug}/zugang.
    const ticketLink = ticketCenterUrl
      || (eventSlug ? `/event/${escapeHtml(eventSlug)}/zugang` : '');
    if (ticketLink) {
      buttons.push(`
        <div class="checkout-outcome-footer-link">
          <a href="${escapeHtml(ticketLink)}" class="checkout-outcome-text-link">
            ${ICONS.ticketBtn}
            <span>${__('Mein Ticket ansehen')}</span>
            ${ICONS.arrow}
          </a>
        </div>
      `);
    }
  }

  return buttons.join('');
}

/**
 * Event-Listener für Buttons
 */
function attachEventListeners(container, onClose, onRetry) {
  // Close Button
  container.querySelectorAll('[data-action="close"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      removeEscHandler(container);
      if (typeof onClose === 'function') {
        onClose();
      } else {
        hideCheckoutSuccess(container);
      }
    });
  });

  // Retry Button
  container.querySelectorAll('[data-action="retry"]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (typeof onRetry === 'function') {
        onRetry();
      }
    });
  });

  // ESC key dismisses the success box
  // Remove any previous handler to prevent accumulation
  removeEscHandler(container);

  function handleEsc(e) {
    if (e.key !== 'Escape') return;
    if (!container.classList.contains('checkout-outcome-visible')) return;
    e.stopPropagation();
    removeEscHandler(container);
    try {
      if (typeof onClose === 'function') {
        onClose();
      } else {
        hideCheckoutSuccess(container);
      }
    } catch (err) {
      if (window.console) console.error('Success-box onClose error:', err);
    }
  }
  container._escHandler = handleEsc;
  document.addEventListener('keydown', handleEsc);
}

/** Remove tracked ESC handler from document */
function removeEscHandler(container) {
  if (container && container._escHandler) {
    document.removeEventListener('keydown', container._escHandler);
    container._escHandler = null;
  }
}

/**
 * Versteckt die Success-Box und stellt Original-Inhalt wieder her
 */
function hideCheckoutSuccess(container) {
  if (!container) return;

  removeEscHandler(container);
  container.classList.remove('checkout-outcome-visible');

  const successBox = container.querySelector('[data-checkout-success]');
  if (successBox) {
    successBox.remove();
  }
}

/**
 * HTML escapen (XSS-Schutz)
 */
function escapeHtml(str) {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// Global exportieren
window.renderCheckoutSuccess = renderCheckoutSuccess;
window.hideCheckoutSuccess = hideCheckoutSuccess;
